import { type ReactNode } from 'react'
import styled from 'styled-components'
import { formatAvailableStores } from '@/features/shared/utils/promotionUtils'
import { removePeriodIfNeeded } from '@/features/shared/utils/formatter'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { Body } from '@shipt/design-system-typography'
import { Box, Column, Row } from '@shipt/design-system-layouts'

type Props = {
  id?: string
  leadingSlot?: ReactNode
  title?: string
  expiresString?: string
  termsApply?: boolean
  numStores?: number
  buttonLabel?: string
  buttonAction?: (event: React.MouseEvent) => void
  surface?: 'default' | 'inverse'
  originalPrice?: string
  isHome: boolean
  subtitle?: string
  textColor?: string | null
}

export const BannerContents = ({
  id,
  leadingSlot,
  title,
  expiresString,
  numStores,
  buttonLabel,
  buttonAction,
  originalPrice,
  surface = 'default',
  isHome,
  subtitle,
  termsApply = false,
  textColor,
}: Props) => {
  if (!title) return null
  return (
    <PromoWrapper textColor={textColor} flexGrow={1} align="center" spacing="sm">
      {Boolean(leadingSlot) && <Box display={{ base: 'none', sm: 'block' }}>{leadingSlot}</Box>}
      <Row flexGrow={1} align="center" justify="space-between" wrap spacing={['xxs', 'lg']}>
        <Row spacing="xs">
          {Boolean(originalPrice) && (
            <BannerText
              surface={surface}
              variant="secondary"
              size={{ base: 'lg', md: 'xl' }}
              lineHeight="default"
              strike
              as="h3"
            >
              {originalPrice}
            </BannerText>
          )}
          <Column>
            <BannerText
              id={`promo_${id}_title`}
              surface={surface}
              size={{ base: 'lg', md: 'xl' }}
              strong
              lineHeight="default"
              as="h3"
              textColor={textColor}
            >
              {removePeriodIfNeeded(title)}
            </BannerText>
            {!!subtitle && (
              <BannerText variant="secondary" surface={surface} size="md" textColor={textColor}>
                {removePeriodIfNeeded(subtitle)}
              </BannerText>
            )}
          </Column>
        </Row>
        <Box>
          <BannerText surface={surface} textColor={textColor} size="md">
            {Boolean(expiresString) && <span>{expiresString}</span>}
            {Boolean(termsApply) && <span>Terms Apply</span>}
            {Boolean(buttonLabel) && (
              <span>
                <PromoButton
                  aria-describedby={`promo_${id}_title`}
                  onClick={buttonAction}
                  surface={surface}
                  textColor={textColor}
                >
                  {buttonLabel}
                </PromoButton>
              </span>
            )}
            {Boolean(isHome && numStores) && <span>{formatAvailableStores(numStores ?? 1)}</span>}
          </BannerText>
        </Box>
      </Row>
    </PromoWrapper>
  )
}

const PromoWrapper = styled(Row)<{ textColor?: string | null }>`
  color: ${({ textColor }) => textColor};
`

const BannerText = styled(Body)<{ textColor?: string | null }>`
  color: ${({ textColor }) => textColor};

  & > span:not(:last-child)::after {
    content: ' · ';
  }
`

const PromoButton = styled(TransparentButton)<{
  surface: 'default' | 'inverse'
  textColor?: string | null
}>`
  color: ${({ theme, surface, textColor }) =>
    textColor ??
    (surface === 'inverse'
      ? theme.color.content.action.inverse
      : theme.button.color.content.default)};
  font-weight: 600;
  text-decoration: underline;
`
